<template>
  <v-layout>
    <v-flex>
      <v-row>
        <v-container fluid pt-0>
          <v-col cols="12" class="py-0">
            <v-row align="center">
              <h1 class="headline">Rooms</h1>
              <v-spacer></v-spacer>
              <div>
                <list-visible-columns-and-filter
                  :visible-columns="visibleColumns"
                  :selectable-headers="selectableHeaders"
                  v-model="search"
                  @change="onVisibleColumnsChange"
                ></list-visible-columns-and-filter>
              </div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" fab dark color="primary" @click="createItem">
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Create New Room</span>
              </v-tooltip>
            </v-row>
          </v-col>
        </v-container>
      </v-row>

      <v-card>
        <v-data-table
          :headers="filteredHeaders"
          :items="items"
          :loading="$apollo.loading"
          v-bind="pagingOptions"
          :search="search"
          item-key="node.id"
          fixed-header
          hide-default-footer
        >
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              :hide-view="true"
              @deleteItem="deleteItem($event)"
              @editItem="editItem($event)"
            ></default-list-actions>
          </template>
          <template v-slot:item.notInUse="{ item }">
            <v-simple-checkbox
              v-model="item.node.notInUse"
              disabled
            ></v-simple-checkbox>
          </template>
        </v-data-table>
      </v-card>
      <v-card>
        <v-dialog v-model="accommodationEditorDialog" persistent width="700">
          <v-card>
            <room-editor
              :accommodation="selectedAccommodation"
              :doSaveAccommodation="doSaveAccommodation"
              :accommodationEditorDialog="accommodationEditorDialog"
              @saved="onSaved($event)"
              @onResetSave="onResetSave"
            ></room-editor>
            <v-card-actions class="grey lighten-4">
              <v-spacer></v-spacer>
              <v-btn color="warning" text @click="closeDialog">Cancel</v-btn>
              <v-btn color="primary" @click="onSaveAccommodation">Save</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import {
  ACCOMMODATION_SERVICE_TYPE_DELETE_MUTATION,
  ALL_ACCOMMODATION_SERVICE_TYPES_SEARCH_QUERY,
} from "../../../../constants/graphql";
import RoomEditor from "./RoomEditor";
import DefaultListActions from "../../shared/DefaultListActions";
import SimpleListMixin from "../../../../mixins/SimpleListMixin";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";

export default {
  name: "RoomsList",

  components: {
    RoomEditor,
    DefaultListActions,
    ListVisibleColumnsAndFilter,
  },
  mixins: [SimpleListMixin],

  data() {
    return {
      search: "",
      accommodationServiceTypes: {},
      selectedAccommodation: {},
      accommodationEditorDialog: false,
      doSaveAccommodation: false,
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "name",
        "code",
        "stdOccupancy",
        "minOccupancy",
        "actions",
      ]),
      headers: [
        {
          text: "Name",
          align: "left",
          value: "node.name",
          sortable: true,
          name: "name",
        },
        {
          text: "Code",
          align: "left",
          value: "node.code",
          sortable: true,
          name: "code",
        },
        {
          text: "Std Occupancy",
          align: "left",
          value: "node.stdOccupancy",
          sortable: true,
          name: "stdOccupancy",
        },
        {
          text: "Min Occupancy",
          align: "left",
          value: "node.minOccupancy",
          sortable: true,
          name: "minOccupancy",
        },
        {
          text: "Max Occupancy",
          align: "left",
          value: "node.maxOccupancy",
          sortable: true,
          name: "maxOccupancy",
        },
        {
          text: "Sales Nominal Code",
          align: "left",
          value: "node.salesNominalCode",
          sortable: true,
          name: "salesNominalCode",
        },
        {
          text: "Purchase Nominal Code",
          align: "left",
          value: "node.purchaseNominalCode",
          sortable: true,
          name: "purchaseNominalCode",
        },
        {
          text: "Tax Code",
          align: "left",
          value: "node.taxCode",
          sortable: true,
          name: "taxCode",
        },
        {
          text: "Not In Use",
          align: "left",
          value: "notInUse",
          sortable: true,
          name: "notInUse",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
    };
  },

  computed: {
    items() {
      return this.accommodationServiceTypes
        ? this.accommodationServiceTypes.edges
        : [];
    },
    filteredHeaders() {
      return this.headers.filter(
        (h) => this.visibleColumns.indexOf(h.name) > -1
      );
    },
  },

  methods: {
    createItem() {
      this.selectedAccommodation = {};
      this.accommodationEditorDialog = true;
    },
    editItem(item) {
      this.selectedAccommodation = item.node;
      this.accommodationEditorDialog = true;
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: ACCOMMODATION_SERVICE_TYPE_DELETE_MUTATION,
          variables: {
            accommodationServiceTypeId: item.node.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", {
        msg: "Accommodation service type removed.",
      });
      if (this.$apollo.queries.accommodationServiceTypes != null) {
        this.$apollo.queries.accommodationServiceTypes.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      this.$store.dispatch("showAppSnackbar", {
        msg: err.graphQLErrors[0].message,
        color: "error",
      });
    },
    onSaved(context) {
      if (this.$apollo.queries.accommodationServiceTypes != null) {
        this.$apollo.queries.accommodationServiceTypes.refetch();
      }
      this.onResetSave();
      this.accommodationEditorDialog = false;
      this.selectedAccommodation = {};
      let msg =
        context == "edit"
          ? "Accommodation service type updated."
          : "Accommodation service type created.";
      this.$store.dispatch("showAppSnackbar", { msg });
    },
    onResetSave() {
      this.doSaveAccommodation = false;
    },
    closeDialog() {
      this.accommodationEditorDialog = false;
      this.selectedAccommodation = {};
    },
    onSaveAccommodation() {
      this.doSaveAccommodation = true;
    },
    showColumn(fieldName) {
      return this.visibleColumns.find((name) => name === fieldName);
    },
  },

  apollo: {
    accommodationServiceTypes: {
      query: ALL_ACCOMMODATION_SERVICE_TYPES_SEARCH_QUERY,
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {},
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/admin/service-types/RoomsList.vue